import React, { useState, useRef, useEffect } from 'react';
import { editFormWithAi } from '../../services/api/formApi';
import './AiSidebar.css';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */

/**
 * Flatten validation errors into human-readable strings with question context.
 */
const flattenValidationErrors = (validationResult, questions) => {
  const errs = [];
  (validationResult.errors || []).forEach(({ questionIndex, errors }) => {
    const q = questions?.[questionIndex];
    const label = q
      ? `Question ${questionIndex + 1} (type=${q.type}, title="${q.title}")`
      : `Question ${questionIndex + 1}`;
    errors.forEach(e => errs.push(`${label}: ${e}`));
  });
  return errs;
};

const SUGGESTIONS = [
  'Add a short text question for full name',
  'Add a 5-star rating question',
  'Make all questions required',
  'Add multiple choice for gender',
  'Add a date picker for date of birth',
  'Add a paragraph for additional comments',
];

/* ─────────────────────────────────────────────
   AiSidebar Component
───────────────────────────────────────────── */

/**
 * Slide-in AI chat sidebar that lets users describe form changes in plain
 * language. Gemini updates the form and the result is passed back via
 * `onFormUpdate`.
 *
 * Props:
 *  - formId      {string}   – used to scope localStorage history
 *  - form        {object}   – current form snapshot (title, description, settings, questions)
 *  - onFormUpdate {function} – called with the new form data on success
 *  - onClose     {function} – called when the sidebar should be dismissed
 */
const AiSidebar = ({ formId, form, onFormUpdate, onClose }) => {
  const HISTORY_KEY = formId ? `formx_ai_history_${formId}` : null;

  // Load full chat history (user + AI messages) from localStorage.
  // Always reset isRetrying so a stale retrying-state never survives a page refresh.
  const loadHistory = () => {
    if (!HISTORY_KEY) return [];
    try {
      const raw = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      return raw.map(m => ({
        ...m,
        time: new Date(m.time),
        isRetrying: false,                          // reset in case page refreshed mid-retry
        _key: m._key ?? `${Date.now()}-${Math.random()}`, // back-compat for old history entries
      }));
    } catch { return []; }
  };

  const [messages, setMessages] = useState(loadHistory);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatLogRef = useRef(null);
  const textareaRef = useRef(null);

  // Persist full chat history to localStorage on every change
  useEffect(() => {
    if (!HISTORY_KEY) return;
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages)); } catch {}
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to latest message
  useEffect(() => {
    if (chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  /**
   * Send a prompt to the AI.
   * @param {string} overridePrompt – Use this instead of the textarea value.
   */
  const handleSend = async (overridePrompt) => {
    const text = (overridePrompt || prompt).trim();
    if (!text || isLoading) return;

    const userMsg = {
      role: 'user',
      text,
      time: new Date(),
      _key: `${Date.now()}-${Math.random()}`,
    };
    setMessages(prev => [...prev, userMsg]);
    setPrompt('');
    setIsLoading(true);

    // Accumulate all user turns (for multi-turn context)
    const accumulatedPrompt = [...messages, userMsg]
      .filter(m => m.role === 'user')
      .map(m => m.text)
      .join(' | ');

    // Snapshot of the current (untouched) form
    const currentForm = {
      title: form.title,
      description: form.description,
      settings: form.settings,
      questions: form.questions,
    };

    try {
      const updatedFormData = await editFormWithAi(accumulatedPrompt, currentForm, null, null);

      const { validateForm } = await import('../../utils/validation');
      const validation = validateForm(updatedFormData);

      if (!validation.isValid) {
        const errList = flattenValidationErrors(validation, updatedFormData.questions);
        setMessages(prev => [...prev, {
          role: 'ai',
          text: 'Something went wrong — the AI returned an invalid form. Your form has NOT been changed.',
          type: 'error',
          time: new Date(),
          _key: `${Date.now()}-${Math.random()}`,
          // ── retry payload (never shown to user, only used internally) ──
          retryAccumulatedPrompt: accumulatedPrompt,         // exact prompt reused on retry
          retryErrors: errList,                              // sent to AI so it knows what to fix
          retryBadFormJson: JSON.stringify(updatedFormData), // the bad form — passed as previousAttemptJson
        }]);
        return;
      }

      onFormUpdate(updatedFormData);
      const qCount = updatedFormData.questions?.length ?? 0;
      setMessages(prev => [...prev, {
        role: 'ai',
        text: `✓ Done! Your form now has ${qCount} question${qCount !== 1 ? 's' : ''}. Review the changes in the builder.`,
        type: 'success',
        time: new Date(),
        _key: `${Date.now()}-${Math.random()}`,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'ai',
        text: `❌ ${err.message || 'Something went wrong. Please try again.'}`,
        type: 'error',
        time: new Date(),
        _key: `${Date.now()}-${Math.random()}`,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Retry after a validation failure.
   * Key rules:
   *  - NO new user message is added to state or localStorage
   *  - The accumulated prompt sent to AI is the SAME one from the original
   *    failed request (stored on the error message) — "Try again" is never
   *    appended to the AI context
   *  - The error bubble is updated IN-PLACE (retrying state → success/error)
   */
  const handleRetry = async (msg) => {
    if (isLoading) return;
    const msgKey = msg._key;

    // Flip the bubble to retrying state — no new message added
    setMessages(prev => prev.map(m =>
      m._key === msgKey ? { ...m, isRetrying: true } : m
    ));
    setIsLoading(true);

    const currentForm = {
      title: form.title,
      description: form.description,
      settings: form.settings,
      questions: form.questions,
    };

    try {
      const updatedFormData = await editFormWithAi(
        msg.retryAccumulatedPrompt, // ← identical to original request, no "Try again" noise
        currentForm,
        msg.retryBadFormJson,       // bad form the AI produced last time
        msg.retryErrors,            // validation errors from that form
      );

      const { validateForm } = await import('../../utils/validation');
      const validation = validateForm(updatedFormData);

      if (!validation.isValid) {
        // Still invalid — update the SAME bubble with fresh retry payload
        const errList = flattenValidationErrors(validation, updatedFormData.questions);
        setMessages(prev => prev.map(m =>
          m._key === msgKey ? {
            ...m,
            isRetrying: false,
            text: 'Still couldn\'t produce a valid form. Your form has NOT been changed.',
            retryErrors: errList,
            retryBadFormJson: JSON.stringify(updatedFormData),
            // retryAccumulatedPrompt stays the same — never grows
          } : m
        ));
        return;
      }

      // Success — replace the error bubble with a success bubble in-place
      onFormUpdate(updatedFormData);
      const qCount = updatedFormData.questions?.length ?? 0;
      setMessages(prev => prev.map(m =>
        m._key === msgKey ? {
          role: 'ai',
          text: `✓ Done! Your form now has ${qCount} question${qCount !== 1 ? 's' : ''}. Review the changes in the builder.`,
          type: 'success',
          time: new Date(),
          _key: msgKey,
        } : m
      ));
    } catch (err) {
      setMessages(prev => prev.map(m =>
        m._key === msgKey ? {
          ...m,
          isRetrying: false,
          text: `❌ ${err.message || 'Something went wrong. Please try again.'}`,
        } : m
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (d) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div className="ai-sidebar-overlay" onClick={onClose} />
      <div className="ai-sidebar">
        {/* Header */}
        <div className="ai-sidebar-header" style={{ position: 'relative' }}>
          <div className="ai-sidebar-title">
            <span className="ai-sparkle">✦</span>
            AI Form Assistant
          </div>
          <p className="ai-sidebar-subtitle">
            Tell me what to add, remove, or change
          </p>
          <button className="ai-close-btn" onClick={onClose} title="Close">✕</button>
        </div>

        {/* Chat log */}
        <div className="ai-chat-log" ref={chatLogRef}>
          {messages.length === 0 && !isLoading ? (
            <div className="ai-welcome">
              <div className="ai-welcome-icon">✦</div>
              <h3>How can I help build your form?</h3>
              <p>
                Describe what you want to add or change. I'll update your form instantly.
              </p>
              <div className="ai-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    className="ai-chip"
                    onClick={() => handleSend(s)}
                    disabled={isLoading}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) =>
                msg.role === 'user' ? (
                  <div key={i} className="ai-msg-user">
                    {msg.text}
                    <div className="ai-msg-meta">{formatTime(msg.time)}</div>
                  </div>
                ) : (
                  <div key={i} className={`ai-msg-ai ${msg.type || ''}`}>
                    <div className="ai-msg-text">{msg.text}</div>

                    {/*
                      Try Again — shown only on error messages that have retry payload.
                      Clicking does NOT add a user bubble; the error bubble updates in-place.
                      "Try again" is never sent to AI or saved to localStorage.
                    */}
                    {msg.retryAccumulatedPrompt && (
                      <button
                        className={`ai-retry-btn${msg.isRetrying ? ' retrying' : ''}`}
                        onClick={() => handleRetry(msg)}
                        disabled={isLoading}
                        title="Retry — validation context sent silently to AI"
                      >
                        {msg.isRetrying
                          ? <><span className="ai-retry-spinner" /> Retrying…</>
                          : '↺ Try Again'
                        }
                      </button>
                    )}

                    <div className="ai-msg-meta">{formatTime(msg.time)}</div>
                  </div>
                )
              )}
              {isLoading && (
                <div className="ai-typing">
                  <span /><span /><span />
                </div>
              )}
            </>
          )}
        </div>

        {/* Input */}
        <div className="ai-input-area">
          <div className="ai-input-row">
            <textarea
              ref={textareaRef}
              className="ai-textarea"
              rows={2}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Add a multiple choice question for preferred contact method…"
              disabled={isLoading}
            />
            <button
              className="ai-send-btn"
              onClick={() => handleSend()}
              disabled={!prompt.trim() || isLoading}
              title="Send"
            >
              {isLoading ? '⏳' : '➤'}
            </button>
          </div>
          <div className="ai-input-hint">Enter to send · Shift+Enter for new line</div>
        </div>
      </div>
    </>
  );
};

export default AiSidebar;
